import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cityAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage } from '../Common/admin';

const CityCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cityCode: '',
    cityNameAr: '',
    cityNameEng: '',
    areaId: '',
    isActive: true,
  });
  const [areas, setAreas] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadAreas = useCallback(async () => {
    try {
      const response = await cityAPI.getAreas();
      setAreas(response.data || []);
    } catch (err) {
      console.error('Failed to load areas:', err);
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.cityCode.trim()) {
      newErrors.cityCode = t('city.create.errors.cityCodeRequired');
    }
    if (!formData.cityNameAr.trim()) {
      newErrors.cityNameAr = t('city.create.errors.nameArRequired');
    }
    if (!formData.cityNameEng.trim()) {
      newErrors.cityNameEng = t('city.create.errors.nameEngRequired');
    }
    if (!formData.areaId) {
      newErrors.areaId = t('city.create.errors.areaRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await cityAPI.create({ ...formData, areaId: parseInt(formData.areaId) });
      setShowSuccessModal(true);
      setTimeout(() => navigate('/city'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('city.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const textField = (name, label, maxLength) => (
    <div className="ad-form-field">
      <label className="ad-label ad-label-required" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        type="text"
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  return (
    <AdminPage
      title={t('city.create.title')}
      headerActions={
        <button type="button" className="ad-btn" onClick={() => navigate('/city')}>
          <i className="fas fa-arrow-right"></i>
          {t('city.create.backToList')}
        </button>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {textField('cityCode', t('city.create.fields.cityCode'), 20)}
            {textField('cityNameAr', t('city.create.fields.nameAr'), 100)}
            {textField('cityNameEng', t('city.create.fields.nameEng'), 100)}

            <div className="ad-form-field">
              <label className="ad-label ad-label-required" htmlFor="areaId">
                {t('city.create.fields.area')}
              </label>
              <select
                id="areaId"
                name="areaId"
                className={`ad-select ${errors.areaId ? 'ad-input-error' : ''}`}
                value={formData.areaId}
                onChange={handleChange}
              >
                <option value="">{t('city.create.fields.area')}</option>
                {areas.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.areaNameEng}
                  </option>
                ))}
              </select>
              {errors.areaId && (
                <span className="ad-error-text">{errors.areaId}</span>
              )}
            </div>
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t('city.create.fields.isActive')}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/city')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t('city.create.save')}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t('city.create.messages.createSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default CityCreate;
